import { EVENT_TYPES, normalizeEntityType } from "../types";
import type { EntityType, EventOption, EventType } from "../types";

/**
 * The "Kind" a step can be given in the step editor. A kind is what the stats
 * read (waits, delays, durations, food and flight times); the step's name is
 * what you see. Most places only need the general kinds, so the editor offers
 * those plus the few that mean something for the place's type.
 */

/** Offered on every step. */
export const GENERAL_KINDS: readonly EventType[] = [
  "WAIT_START",
  "WAIT_END",
  "MAIN_EVENT_START",
  "MAIN_EVENT_END",
  "PROGRESS_UPDATE",
  "DELAY_START",
  "DELAY_END",
];

/** Offered on top of the general kinds, by place type. */
const TYPE_KINDS: Partial<Record<EntityType, readonly EventType[]>> = {
  // Wait ends at Seated or Order Placed; food time runs Order Placed → Received / Picked Up.
  RESTAURANT: ["SEATED", "ORDER_PLACED", "ORDER_RECEIVED", "PICKED_UP"],
  EXPERIENCE: ["SEATED", "PRESHOW_START", "PRESHOW_END"],
  ATTRACTION: ["PRESHOW_START", "PRESHOW_END"],
  // Boarding and Departed set the scheduled-time stats; Landed keeps a flight live until Off Plane.
  TRANSPORTATION: ["BOARDING", "DEPARTED", "LANDED"],
  SHIP: ["BOARDING", "DEPARTED", "LANDED"],
};

/**
 * Kinds nothing reads any more: each behaves exactly like the kind it maps to
 * (same category, no stat of its own). Steps and logs that already carry one
 * keep it, so old activities still line up with their steps; the editor just
 * shows and offers the kind it stands for.
 */
export const RETIRED_KINDS: Partial<Record<EventType, EventType>> = {
  CHECK_IN: "PROGRESS_UPDATE",
  SECURITY: "PROGRESS_UPDATE",
  RIDE_REQUESTED: "WAIT_START",
  RIDE_ACCEPTED: "PROGRESS_UPDATE",
  VEHICLE_ARRIVED: "PROGRESS_UPDATE",
  IN_TRANSIT: "MAIN_EVENT_START",
  ARRIVED: "MAIN_EVENT_END",
  DINING_DONE: "MAIN_EVENT_END",
};

export const KIND_LABELS: Partial<Record<EventType, string>> = {
  WAIT_START: "Wait starts",
  WAIT_END: "Wait ends",
  MAIN_EVENT_START: "Starts",
  MAIN_EVENT_END: "Ends",
  PROGRESS_UPDATE: "Along the way",
  DELAY_START: "Delay starts",
  DELAY_END: "Delay ends",
  SEATED: "Seated",
  ORDER_PLACED: "Order placed",
  ORDER_RECEIVED: "Order received",
  PICKED_UP: "Picked up",
  PRESHOW_START: "Preshow starts",
  PRESHOW_END: "Preshow ends",
  BOARDING: "Boarding",
  DEPARTED: "Departed",
  LANDED: "Landed",
};

/** The kind a stored event type stands for today. */
export function currentKind(t: EventType): EventType {
  return RETIRED_KINDS[t] ?? t;
}

export function kindLabel(t: EventType): string {
  return KIND_LABELS[currentKind(t)] ?? t;
}

/**
 * The kinds to offer for a type's steps: the general ones, the type's own, and
 * any special kind the list already uses (so a step never loses its kind).
 */
export function kindsFor(
  entityType: EntityType | string | null | undefined,
  options: Pick<EventOption, "eventType">[] = [],
): EventType[] {
  const type = normalizeEntityType(entityType);
  const wanted = new Set<EventType>(GENERAL_KINDS);
  for (const k of (type && TYPE_KINDS[type]) || []) wanted.add(k);
  for (const o of options) wanted.add(currentKind(o.eventType));
  // The general kinds first, in the order a step runs; then the special ones.
  return [...GENERAL_KINDS, ...EVENT_TYPES.filter((t) => wanted.has(t) && !GENERAL_KINDS.includes(t))];
}
